// Footer.tsx - Enhanced version with improved responsiveness and accessibility
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Footer.css';
import logo from '../assets/logo.png';

const Footer: React.FC = () => {
  const location = useLocation();
  const gaazzeeBoRef = useRef<HTMLSpanElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [currentYear] = useState(new Date().getFullYear());
  const [isHovering, setIsHovering] = useState(false);
  const [isPouring, setIsPouring] = useState(false);
  const [streamLength, setStreamLength] = useState(80);
  const [isMobile, setIsMobile] = useState(false);
  const bottleRef = useRef<HTMLDivElement>(null);
  const glassRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Check if on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = document.getElementById('bourbon-pour-audio') as HTMLAudioElement;
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
    }
    
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
    
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    
    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // Calculate the exact distance between bottle and glass for precise pouring
  useEffect(() => {
    const calculatePourDistance = () => {
      if (bottleRef.current && glassRef.current) {
        const bottleRect = bottleRef.current.getBoundingClientRect();
        const glassRect = glassRef.current.getBoundingClientRect();
        
        const bottleNeckRight = bottleRect.right;
        const glassLeft = glassRect.left + (isMobile ? 5 : 10);
        
        const bottleNeckTop = bottleRect.top + 15;
        const glassTop = glassRect.top + 20;
        
        const distance = Math.max(glassLeft - bottleNeckRight, isMobile ? 30 : 40);
        setStreamLength(distance);
        
        if (footerRef.current) {
          const splashDrops = footerRef.current.querySelector('.bourbon-splash-drops');
          if (splashDrops) {
            const splashLeft = isMobile ? glassLeft - 15 : glassLeft - 20;
            (splashDrops as HTMLElement).style.left = `${splashLeft}px`;
            
            footerRef.current.style.setProperty('--pour-distance', `${distance}px`);
            footerRef.current.style.setProperty('--bottle-neck-top', `${bottleNeckTop}px`);
            footerRef.current.style.setProperty('--glass-top', `${glassTop}px`);
          }
        }
      }
    };

    calculatePourDistance();
    window.addEventListener('resize', calculatePourDistance);
    const timer = setTimeout(calculatePourDistance, 500);
    
    return () => {
      window.removeEventListener('resize', calculatePourDistance);
      clearTimeout(timer);
    };
  }, [isMobile]);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  // Handle audio playback
  useEffect(() => {
    if (isHovering && audioRef.current && hasUserInteracted) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
  }, [isHovering, hasUserInteracted]);

  // Track mouse position for hover effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!footerRef.current) return;
      
      const footerRect = footerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - footerRect.left;
      const mouseY = e.clientY - footerRect.top;
      
      const xPercent = (mouseX / footerRect.width) * 100;
      const yPercent = (mouseY / footerRect.height) * 100;
      
      footerRef.current.style.setProperty('--mouse-x', `${xPercent}%`);
      footerRef.current.style.setProperty('--mouse-y', `${yPercent}%`);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Handle pouring animation with delay
  useEffect(() => {
    let pourTimer: NodeJS.Timeout;
    
    if (isHovering) {
      const delay = isMobile ? 200 : 300;
      pourTimer = setTimeout(() => {
        setIsPouring(true);
      }, delay);
    } else {
      setIsPouring(false);
    }
    
    return () => clearTimeout(pourTimer);
  }, [isHovering, isMobile]);

  // Text scramble effect for "gaazzeebo"
  useEffect(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_+";
    let interval: NodeJS.Timeout | null = null;
    
    const startTextScramble = () => {
      let iteration = 0;
      const originalText = "gaazzeebo";
      
      if (interval) clearInterval(interval);
      
      interval = setInterval(() => {
        if (!gaazzeeBoRef.current) return;
        
        gaazzeeBoRef.current.innerText = originalText
          .split("")
          .map((letter, index) => {
            if (index < iteration) return originalText[index];
            return letters[Math.floor(Math.random() * letters.length)];
          })
          .join("");
        
        if (iteration >= originalText.length && interval) {
          clearInterval(interval);
        }
        
        iteration += 1 / 3;
      }, 50);
    };
    
    startTextScramble();
    const animationInterval = setInterval(startTextScramble, 15000);
    
    return () => {
      if (interval) clearInterval(interval);
      clearInterval(animationInterval);
    };
  }, []);

  // Intersection Observer for animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Toggle pouring animation
  const togglePouring = () => {
    setIsHovering(!isHovering);
  };

  return (
    <footer className={`site-footer ${isVisible ? 'visible' : ''} ${isMobile ? 'mobile' : ''}`} ref={footerRef}>
      <audio id="bourbon-pour-audio" preload="auto">
        <source src="/assets/bourbon-pour.mp3" type="audio/mpeg" />
      </audio>
      
      <div className="barrel-background">
        <div className="barrel-stave stave1"></div>
        <div className="barrel-stave stave2"></div>
        <div className="barrel-stave stave3"></div>
        <div className="barrel-stave stave4"></div>
        <div className="barrel-stave stave5"></div>
        <div className="barrel-ring ring-top"></div>
        <div className="barrel-ring ring-bottom"></div>
      </div>
      
      <div className="bourbon-splash"></div>
      
      {/* Logo Section */}
      <div className="footer-logo-section">
        <Link to="/" className="footer-logo-link">
          <img src={logo} alt="Sip'N'Security Logo" className="footer-logo" />
          <h3 className="footer-brand-name">Sip'N'Security</h3>
        </Link>
        <p className="footer-tagline">Where Bourbon Meets Cybersecurity</p>
      </div>
      
      <nav className="footer-navigation" aria-label="Footer navigation">
        <ul>
          <li><Link to="/" aria-label="Home">Home</Link></li>
          <li><Link to="/store" aria-label="Store">Store</Link></li>
          <li><Link to="/podcast" aria-label="Podcast">Podcast</Link></li>
          <li><Link to="/blog" aria-label="Blog">Blog</Link></li>
          <li><Link to="/contact" aria-label="Contact">Contact</Link></li>
        </ul>
      </nav>
      
      <div className="footer-divider">
        <div className="divider-line"></div>
        <div className="bourbon-drop"></div>
        <div className="divider-line"></div>
      </div>
      
      <div className="social-links">
        <a href="https://www.youtube.com/@sipnsecurity9497/featured" target="_blank" rel="noopener noreferrer" className="social-icon youtube" aria-label="YouTube">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </a>
        <a href="https://www.instagram.com/sipnsecurity" target="_blank" rel="noopener noreferrer" className="social-icon instagram" aria-label="Instagram">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
          </svg>
        </a>
        <a href="https://open.spotify.com/show/2TDqcJbEoNkITEz23TkqNS" target="_blank" rel="noopener noreferrer" className="social-icon spotify" aria-label="Spotify">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        </a>
      </div>
      
      <div className="copyright">
        <p>© {currentYear} Sip'N'Security. All Rights Reserved.</p>
      </div>
      
      <div className="spectacular-powered-by">
        <div
          className="bourbon-bottle"
          ref={bottleRef}
          onClick={togglePouring}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onTouchStart={() => setIsHovering(true)}
          onTouchEnd={() => setIsHovering(false)}
          role="button"
          tabIndex={0}
          aria-label="Pour bourbon"
        >
          <div className="bottle-neck"></div>
          <div className="bottle-shoulder"></div>
          <div className="bottle-body"></div>
          <div className="bottle-label">
            <span className="label-text">SIPNSECURITY</span>
          </div>
          <div className="bottle-base"></div>
          
          <div 
            className={`bourbon-stream ${isPouring ? 'pouring' : ''}`} 
            style={isPouring ? { width: `${streamLength}px` } : undefined}
            aria-hidden="true"
          ></div>
          <div className={`bourbon-splash-drops ${isPouring ? 'splashing' : ''}`} aria-hidden="true">
            <div className="splash-drop drop1"></div>
            <div className="splash-drop drop2"></div>
            <div className="splash-drop drop3"></div>
            <div className="splash-drop drop4"></div>
            <div className="splash-drop drop5"></div>
          </div>
        </div>
        
        <div className="bourbon-glass" ref={glassRef} aria-hidden="true">
          <div className={`glass-shine ${isPouring ? 'shining' : ''}`}></div>
          <div className={`bourbon-fill ${isPouring ? 'filling' : ''}`}></div>
        </div>
        
        <div className="powered-by-text">
          <span className="powered-text">Powered By</span>
          <a href="https://gaazzeebo.net" 
             target="_blank" 
             rel="noopener noreferrer"
             className="gaazzeebo-link"
             onMouseEnter={() => setIsHovering(true)}
             onMouseLeave={() => setIsHovering(false)}
             onTouchStart={() => setIsHovering(true)}
             onTouchEnd={() => setIsHovering(false)}
             aria-label="Visit gaazzeebo.net">
            <span ref={gaazzeeBoRef} className="gaazzeebo-text">gaazzeebo</span>
            
            <div className={`ember-particles ${isHovering ? 'active' : ''}`} aria-hidden="true">
              <div className="ember e1"></div>
              <div className="ember e2"></div>
              <div className="ember e3"></div>
              <div className="ember e4"></div>
              <div className="ember e5"></div>
              <div className="ember e6"></div>
            </div>
          </a>
        </div>
      </div>
      
      <div className="flame-container" aria-hidden="true">
        <div className="flame-wrapper">
          <div className="flame red"></div>
          <div className="flame orange"></div>
          <div className="flame gold"></div>
          <div className="flame white"></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;